/*
  ew-option-radiogroup.js — Esse Werks shared single-select option-radiogroup
  a11y controller. ONE source of truth for the PDP option-pill selection
  mechanism (roving tabindex + aria-checked + arrow-key navigation).

  Extracted verbatim from main-product-standard.liquid's inline handlers so
  every PDP that offers single-select variant options consumes the same,
  ARIA-correct behavior instead of hand-rolling its own.

  --- MARKUP CONTRACT (the consumer section renders this) ---
  A radiogroup row:
      <div data-ew-option-row role="radiogroup"
           aria-label="..." data-option-index="N"> ... </div>
  Each option pill inside it:
      <button type="button" role="radio"
              aria-checked="true|false"      (exactly one true per row, or none)
              tabindex="0|-1"                (the lone tab stop is the checked
                                              pill; if none checked, the FIRST
                                              pill carries it — set server-side)
              data-ew-option-button
              data-option-index="N"          (matches the row's data-option-index)
              data-option-value="...">

  role="radio" REQUIRES aria-checked — never aria-pressed (which SRs ignore on
  a radio). The consumer must render aria-checked, not aria-pressed.

  --- API ---
  window.EWOptionRadiogroup.attach(root, onChange)
    For each [data-ew-option-row][role="radiogroup"] under `root`, wires its
    [data-ew-option-button][role="radio"] pills:
      • click on a pill  → within THAT ROW, check the clicked pill
                           (aria-checked="true" + tabindex="0") and uncheck all
                           siblings (aria-checked="false" + tabindex="-1"), move
                           focus to it, then call onChange(clickedBtn, row).
                           Enter/Space are handled natively by the <button>
                           firing a click — no extra key handler needed.
      • keydown on row   → ArrowRight/ArrowDown = next pill (wrap to first);
                           ArrowLeft/ArrowUp = prev pill (wrap to last). Current
                           pill = the aria-checked one, else pills[0]. Same
                           check/tabindex/focus update + onChange; preventDefault.
                           Any other key is ignored.

  This module NEVER resolves variants, prices, images, or cart state — the
  section's onChange owns all of that. Selection updates are scoped to the row.

  Consumers: main-product-standard, main-product-apparel, main-product-turbo,
  main-product-gift-card.

  Vanilla / ES5-safe (Array.prototype.forEach.call / .slice.call); no deps.
*/
(function () {
  'use strict';

  /* Within a single row, mark `target` as the checked pill (lone tab stop) and
     reset every sibling. Scoped to the row, never the document. */
  function select(row, target) {
    var pills = row.querySelectorAll('[data-ew-option-button][role="radio"]');
    Array.prototype.forEach.call(pills, function (b) {
      var isTarget = b === target;
      b.setAttribute('aria-checked', isTarget ? 'true' : 'false');
      b.setAttribute('tabindex', isTarget ? '0' : '-1');
    });
  }

  function attach(root, onChange) {
    if (!root) return;
    var hasOnChange = typeof onChange === 'function';

    var rows = root.querySelectorAll('[data-ew-option-row][role="radiogroup"]');
    Array.prototype.forEach.call(rows, function (row) {

      /* Pill click → check it + give it the lone tab stop, uncheck/-1 its
         siblings, move focus to it, then hand off to the section's onChange. */
      var pills = row.querySelectorAll('[data-ew-option-button][role="radio"]');
      Array.prototype.forEach.call(pills, function (btn) {
        btn.addEventListener('click', function () {
          select(row, btn);
          btn.focus();
          if (hasOnChange) onChange(btn, row);
        });
      });

      /* Roving tabindex: arrow-key navigation across the row's pills.
         ArrowRight/Down → next (wrap to first); ArrowLeft/Up → prev (wrap
         to last). current = the aria-checked pill, or pills[0] if none. */
      row.addEventListener('keydown', function (e) {
        var rowPills = Array.prototype.slice.call(
          row.querySelectorAll('[data-ew-option-button][role="radio"]')
        );
        if (!rowPills.length) return;
        var current = row.querySelector('[data-ew-option-button][aria-checked="true"]');
        var idx = current ? rowPills.indexOf(current) : 0;
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          next = rowPills[(idx + 1) % rowPills.length];
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          next = rowPills[(idx - 1 + rowPills.length) % rowPills.length];
        }
        if (next) {
          e.preventDefault();
          select(row, next);
          next.focus();
          if (hasOnChange) onChange(next, row);
        }
      });
    });
  }

  window.EWOptionRadiogroup = { attach: attach };
})();
